/**
 * The homepage's sections, ported from `Take B - Bluebird.dc.html`.
 *
 * Each exported section renders a `data-section` attribute that
 * `script/design_diff.mjs` pairs with a selector into the comp. If you rename
 * or remove one, the harness reports MISSING for that section rather than
 * quietly diffing against the wrong block.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { A, Button, H2, PhotoSlot, Wrap } from "./primitives";
import { GoogleRatingBadge, StarRating } from "./GoogleReviews";
import { color, font, maxWidth, radius, shadow, space, type } from "@/lib/design-tokens";
import { cta, shop } from "@/lib/copy/shop";
import { home } from "@/lib/copy/home";
import { googleReviewProof } from "@/lib/copy/reviews";
import { AiActions } from "@/components/shared/AiActions";

/**
 * The hero's cross-fade sequence. Order is the display order.
 *
 * Every entry needs 1200/1800/2600px renditions in public/images/place/,
 * emitted by script/photo_place.mjs — a missing width silently 404s only at the
 * viewport that requests it, which is the kind of bug that reaches production.
 */
const HERO_FRAMES = [
  {
    name: "hero-family",
    alt: "A parent riding a cargo bike with two children in the box",
  },
  {
    name: "hero-family-2",
    alt: "A child looking into the box of a cargo bike",
  },
] as const;

/** What the grid needs to know about a bike, whatever the source. */
export interface GridBike {
  name: string;
  price: string;
  /** Mono caption describing the shot that belongs here, when there is no photo. */
  shot: string;
  shotInUse: string;
  available: boolean;
  sold: boolean;
  href: string;
  /**
   * The real product photograph, when the catalog has one.
   *
   * The comp draws every card as a placeholder hatch, because no photography
   * existed when it was made — and the design-fixture the pixel diff uses has
   * no images, so the diff still compares hatch against hatch and stays exact.
   * Live traffic gets the real photo. A visitor should not be shown a caption
   * describing a picture we already have.
   */
  imageUrl?: string | null;
  imageAlt?: string | null;
  imageSrcSet?: string;
  imageWidth?: number | null;
  imageHeight?: number | null;
  /**
   * The card's ground, measured from the photograph's own corners at ingest.
   *
   * Null when the asset is a transparent cutout, or when its ground is not flat
   * (Benno shoots on a lit gradient, not a sweep) — both fall back to white.
   * See script/photo_ingest.mjs for why this is a measurement and not a
   * per-brand constant.
   */
  imageGround?: string | null;
  imageFit?: "contain" | "cover";
  imageFallbacks?: Array<{
    url?: string;
    srcSet?: string;
    altText?: string | null;
    width?: number | null;
    height?: number | null;
    ground?: string | null;
    fit?: "contain" | "cover";
  }>;
  /** The lifestyle frame revealed on hover. Absent for 4 of the 44 listings. */
  hoverUrl?: string | null;
  hoverSrcSet?: string;
}

/* --- Hero ----------------------------------------------------------------- */

/**
 * The rotating destination.
 *
 * Reimplemented from the comp's behavior rather than ported from `support.js`:
 * one word at a time, wiping in from the left, advancing on a timer that pauses
 * while the pointer is over the headline and while the tab is hidden.
 *
 * `frozen` stops it on the first destination. `script/design_diff.mjs` sets
 * `?__freeze=1` so the comp and the build are guaranteed to be showing the same
 * word at screenshot time — otherwise the largest text on the page diffs
 * against whatever the rotation happened to be on, every run.
 */
function RotatingDestination({ frozen }: { frozen: boolean }) {
  const [i, setI] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    if (frozen) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const t = window.setInterval(() => {
      if (document.hidden || paused.current) return;
      setI((n) => (n + 1) % home.destinations.length);
    }, home.rotationMs);
    return () => window.clearInterval(t);
  }, [frozen]);

  return (
    <h1
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
      style={{
        margin: 0,
        fontWeight: 800,
        lineHeight: 1.02,
        fontSize: type.hero,
        letterSpacing: "-.015em",
        maxWidth: "980px",
      }}
    >
      {home.hero.headlinePrefix}
      <span style={{ display: "block", marginTop: ".1em", color: color.gold }}>
        <span key={i} className="bb-destination">
          {home.destinations[i]}
        </span>
      </span>
    </h1>
  );
}

/**
 * The hero's cross-fading backdrop.
 *
 * Two photographs, each held then dissolved into the next. The headline
 * beneath already rotates through twelve destinations, and a single frozen
 * image under a changing sentence reads as a slideshow that broke.
 *
 * These are geographically neutral Urban Arrow press photographs, published
 * under the authorized-not-licensed decision. The California hills frame was
 * removed because it visibly contradicted the site's Boulder positioning. See
 * public/images/place/README.md for the source and replacement rule.
 *
 * Deliberately slower than the headline (2.4s): a photograph that changed as
 * often as a word would be a strobe. 7 seconds is roughly three destinations
 * per frame, so neither cycle lands on the same beat twice.
 */
function HeroBackdrop({ frozen }: { frozen: boolean }) {
  const [i, setI] = useState(0);

  useEffect(() => {
    // `frozen` is ?__freeze=1, set by script/design_diff.mjs so the pixel
    // harness always compares the same frame.
    if (frozen || HERO_FRAMES.length < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = window.setInterval(() => {
      // Pause while the tab is hidden — a background tab burning through a
      // cross-fade is wasted decode work on someone's battery.
      if (document.hidden) return;
      setI((n) => (n + 1) % HERO_FRAMES.length);
    }, 7000);
    return () => window.clearInterval(t);
  }, [frozen]);

  return (
    <>
      {HERO_FRAMES.map((f, n) => (
        <img
          key={f.name}
          src={`/images/place/${f.name}-1800.jpg`}
          srcSet={
            `/images/place/${f.name}-1200.jpg 1200w, ` +
            `/images/place/${f.name}-1800.jpg 1800w, ` +
            `/images/place/${f.name}-2600.jpg 2600w`
          }
          sizes="100vw"
          alt={n === 0 ? f.alt : ""}
          // Only the first frame blocks render; the rest arrive while the
          // visitor is reading, well before the 7-second turn.
          aria-hidden={n === 0 ? undefined : "true"}
          loading={n === 0 ? "eager" : "lazy"}
          width={2600}
          height={1733}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            opacity: n === i ? 1 : 0,
            // Long enough to read as a dissolve rather than a cut. The
            // stabilization CSS in every harness zeroes this, so the diff sees
            // a static frame.
            transition: "opacity 1.2s ease-in-out",
          }}
        />
      ))}
    </>
  );
}

export function Hero({ frozen }: { frozen: boolean }) {
  return (
    <section
      style={{
        position: "relative",
        background: `repeating-linear-gradient(45deg,${color.hatchBlueLight},${color.hatchBlueLight} 14px,${color.hatchBlueDark} 14px,${color.hatchBlueDark} 28px)`,
        minHeight: "clamp(520px,64vh,760px)",
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      {/*
        The hero photograph: a parent riding with two kids in the box, which is
        what the comp's own placeholder note asks for and what the headline is
        about.

        ⚠️ PROVENANCE — this one is NOT the shop's own. It is an Urban Arrow
        press photograph, shot in the Netherlands, published under the same
        authorized-not-licensed decision as the product photography (see
        public/images/bikes/README.md). It is a stand-in that makes the page
        read correctly today, and it is exactly the substitution
        docs/design/PHOTOGRAPHY-SOURCING-2026-08-14.md warns is NOT what makes a
        shop look local: no Boulder, no Flatirons, not Ryan's customers.

        The shot that belongs here is on the list for Ryan's camera — a family
        riding somewhere recognizably Boulder. When it lands, swap the src and
        delete this note. Do not let a Dutch garden path become permanent
        because it looks fine.
      */}
      <HeroBackdrop frozen={frozen} />
      {/* The scrim that keeps the headline legible over the photograph. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(0,40,104,.94) 0%, rgba(0,40,104,.82) 45%, rgba(0,40,104,.35) 100%)",
        }}
      />
      {/* The comp's mono caption described the photograph that belonged here.
          It is gone now that the photograph exists — a label reading
          "background photo — parent + two kids on a longtail" printed over a
          parent and two kids is the placeholder outliving its purpose. */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth,
          margin: "0 auto",
          boxSizing: "border-box",
          padding: `clamp(72px,12vw,140px) ${space.gutter} clamp(44px,6vw,72px)`,
        }}
      >
        <RotatingDestination frozen={frozen} />
        {/*
          One column, one reading order: headline → sentence → action → proof.

          This was a two-column row (sentence left, a stacked column of actions
          right) that bottom-aligned the two. The right column had grown to four
          separate outlined objects — the button pair, the boxed Google badge,
          the two AI provider pills under their own uppercase label, and the
          disclaimer beneath those — so it stood ~185px tall against a ~108px
          paragraph. Bottom-aligning a tall column against a short one is what
          opened the ~70px dead band between the headline and the paragraph at
          1440, and it is why the first viewport read as a control panel laid
          over a photograph rather than as one composition.

          Two things left the hero to fix it. The AI research actions moved to
          the foot of the promise strip, where comparing bikes is what the
          section is already arguing for; they are a pre-visit research aid, and
          the only conversion this site has is a visit. The rating dropped its
          box (see the `heroPlain` variant) so the proof reads as a line under
          the button it supports instead of as a third control.

          What stays is the hero's whole budget: one headline, one sentence, one
          CTA pair, one proof line. The right half of the frame is now empty on
          purpose — it is the only part of the photograph the headline is not
          already sitting on.
        */}
        <p
          style={{
            fontSize: type.heroBody,
            lineHeight: 1.55,
            maxWidth: "560px",
            margin: "clamp(24px,3vw,32px) 0 0",
            color: color.onBlue,
          }}
        >
          {home.hero.body}
        </p>
        <div
          data-testid="home-hero-actions"
          style={{
            display: "flex",
            gap: "14px",
            flexWrap: "wrap",
            marginTop: "clamp(24px,3vw,32px)",
          }}
        >
          <Button href={cta.testRideHref} variant="red" style={{ fontSize: "16px", padding: "15px 26px" }}>
            {home.hero.primary}
          </Button>
          <Button
            href={cta.seeBikesHref}
            variant="ghost"
            style={{
              border: "2px solid rgba(255,255,255,.55)",
              fontSize: "16px",
              padding: "13px 26px",
            }}
          >
            {home.hero.secondary}
          </Button>
        </div>
        {/* Tight to the buttons, not evenly spaced from them: the rating is
            evidence for the action directly above it, and equal gaps would read
            as a third, unrelated row. */}
        <div style={{ marginTop: "16px" }}>
          <GoogleRatingBadge variant="heroPlain" />
        </div>
      </div>
    </section>
  );
}

/* --- Promise strip -------------------------------------------------------- */

/**
 * The strip's marks.
 *
 * Drawn stroke SVGs on a 24px grid, replacing the ✓ this section used to repeat
 * three times. One glyph three times labels nothing — it says "three things"
 * and then makes the reader read all three to find out which one they wanted.
 * A checkmark is also a text dingbat: it renders in whatever font resolves it,
 * which is not Archivo, and it is the one piece of the page whose weight and
 * size nobody controls.
 *
 * Stroked in `currentColor` so the parent owns the ink, and sized by the
 * caller, so the same mark works at 24px inside a gold disc and at 14px inside
 * the location chip.
 */
const MARK = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
} as const;

/** The bikes on the floor. */
function BikeMark({ size = 24 }: { size?: number }) {
  return (
    <svg {...MARK} width={size} height={size} strokeWidth={1.6}>
      <circle cx="5.6" cy="16.4" r="3.9" />
      <circle cx="18.4" cy="16.4" r="3.9" />
      <path d="M5.6 16.4H12" />
      <path d="M12 16.4L10.3 8.6" />
      <path d="M10.3 8.6H15.6" />
      <path d="M15.6 8.6L12 16.4" />
      <path d="M15.6 8.6L18.4 16.4" />
      <path d="M9.2 7.9H11.4" />
      <path d="M14.4 7.4H17.2" />
    </svg>
  );
}

/** The free thirty minutes. */
function StopwatchMark({ size = 24 }: { size?: number }) {
  return (
    <svg {...MARK} width={size} height={size} strokeWidth={1.6}>
      <circle cx="12" cy="13.6" r="7.4" />
      <path d="M12 9.6v4h3.2" />
      <path d="M9.7 2.6h4.6" />
      <path d="M12 2.6v3.6" />
    </svg>
  );
}

/**
 * Service.
 *
 * The outer ring is load-bearing: teeth radiating from a small disc read as a
 * sun, which is what the first draft of this shipped as.
 */
function GearMark({ size = 24 }: { size?: number }) {
  return (
    <svg {...MARK} width={size} height={size} strokeWidth={1.6}>
      <circle cx="12" cy="12" r="6.6" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M12 5.4V2.6M12 18.6v2.8M18.6 12h2.8M5.4 12H2.6M16.67 7.33l1.98-1.98M7.33 16.67l-1.98 1.98M16.67 16.67l1.98 1.98M7.33 7.33L5.35 5.35" />
    </svg>
  );
}

/** The place. */
function PinMark({ size = 24 }: { size?: number }) {
  return (
    <svg {...MARK} width={size} height={size} strokeWidth={2}>
      <path d="M12 21.2s6.8-5.6 6.8-10.6a6.8 6.8 0 1 0-13.6 0c0 5 6.8 10.6 6.8 10.6z" />
      <circle cx="12" cy="10.4" r="2.5" />
    </svg>
  );
}

/**
 * Positional, by design: the marks pair with `home.promise.points` in order.
 *
 * A `points` entry rewritten in place keeps its mark, which is the common case
 * (a copy pass). A REORDERED or added point needs this reordered with it — so
 * the fallback is the first mark rather than nothing, because a card with no
 * mark is a layout hole and a card with the wrong mark is a copy bug someone
 * will actually see and report.
 */
const POINT_MARKS = [BikeMark, StopwatchMark, GearMark];

/**
 * The promise strip — who the shop is, then the three reasons to come in.
 *
 * Redesigned 2026-08-19 from four options Aaron reviewed on a canvas
 * (docs/design/promise-strip/, published as an editable design). He picked the
 * tinted band, for the reason it was drawn: the section is a white block
 * between a saturated blue hero and a white bike grid, so it had no edges and
 * the three points read as a footnote under the paragraph rather than as the
 * argument for visiting. `color.surface` gives it a top and a bottom, and the
 * points become cards in the same card language the testimonials already use
 * further down the page.
 *
 * ⚠️ Exactly one `<p>` may live in this section. script/copy_identity_check.mjs
 * reads `[data-section="promise"] p` and Playwright's strict mode throws on a
 * second match — the card bodies are `<div>`s for that reason, not by accident.
 *
 * This diverges from the comp on purpose and is named in the KNOWN_DEVIATIONS
 * tables of script/design_diff.mjs and script/width_sweep.mjs.
 */
export function PromiseStrip() {
  return (
    <section data-section="promise" style={{ background: color.surface }}>
      <Wrap padY={space.sectionYTight}>
        <div data-reveal style={{ maxWidth: "760px", margin: "0 auto", textAlign: "center" }}>
          {/* The location chip. White on the tint so it reads as a stamp on the
              band rather than as the first line of the paragraph — which is what
              the 12px mono eyebrow it replaces was doing. */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "9px",
              padding: "7px 14px",
              background: color.white,
              border: `1px solid ${color.borderCard}`,
              borderRadius: radius.chip,
              fontFamily: font.mono,
              fontSize: "12px",
              letterSpacing: ".13em",
              textTransform: "uppercase",
              color: color.blue,
            }}
          >
            <PinMark size={14} />
            {home.promise.eyebrow}
          </span>

          {/* Tracking and leading are tightened past the shared H2's defaults, on
              this instance only. That is what Aaron approved on the artboard
              (docs/design/promise-strip/TintedCards.dc.html), and it is the one
              heading on the site that has to hold its own against a card row
              directly beneath it. H2's own -.01em / normal stays untouched —
              "The bikes" and "What riders say" are unchanged. */}
          <H2
            style={{
              marginTop: "20px",
              color: color.ink,
              letterSpacing: "-.015em",
              lineHeight: 1.1,
            }}
          >
            {home.promise.heading}
          </H2>

          <p
            style={{
              margin: "18px auto 0",
              maxWidth: "700px",
              fontSize: type.heroBody,
              lineHeight: 1.5,
              fontWeight: 500,
              color: color.inkMuted,
              // `balance` rather than `pretty`: this is a short centered block,
              // and the failure it fixes is the last line orphaning two words
              // under a full-width one, not a single bad break mid-paragraph.
              textWrap: "balance",
            }}
          >
            {home.promise.lede}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(240px,100%),1fr))",
            gap: space.gridCol,
            marginTop: "clamp(32px,4vw,52px)",
          }}
        >
          {home.promise.points.map((p, i) => {
            const Mark = POINT_MARKS[i] ?? POINT_MARKS[0];
            return (
              <div
                key={p.title}
                data-reveal="rise"
                data-reveal-stagger=""
                style={{
                  background: color.white,
                  border: `1px solid ${color.borderCard}`,
                  borderRadius: radius.panel,
                  boxShadow: shadow.card,
                  padding: "clamp(22px,2.6vw,28px)",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    width: "46px",
                    height: "46px",
                    borderRadius: "50%",
                    background: color.gold,
                    color: color.blue,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Mark />
                </span>
                <div
                  style={{
                    marginTop: "18px",
                    fontSize: "19px",
                    fontWeight: 800,
                    letterSpacing: "-.005em",
                    color: color.ink,
                  }}
                >
                  {p.title}
                </div>
                <div style={{ marginTop: "8px", fontSize: "15.5px", lineHeight: 1.55, color: color.inkMuted }}>
                  {p.body}
                </div>
              </div>
            );
          })}
        </div>

        {/*
          The AI research actions, moved here from the hero on 2026-09-01.

          They are a pre-visit research aid: draft a question, take it to
          ChatGPT or Claude, come back knowing which bike to ask about. That is
          the same argument the three cards above make — compare the types, then
          come ride one — so this is where the offer reads as help rather than
          as a fourth control competing with the test-ride button.

          In the hero it cost four elements (an uppercase label, two bordered
          pills, and a disclaimer) for a secondary action, sitting directly
          under the one action the site actually converts on. Placed before the
          address line rather than after it, so the section still closes on
          where the building is and when it is open.
        */}
        <div
          data-reveal
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "clamp(30px,3.6vw,44px)",
          }}
        >
          <AiActions surface="homepage" variant="band" />
        </div>

        {/* Where the building is and when it is open — rendered from `shop`, so
            this is the footer's copy rather than a second version of it. Ryan
            confirmed Tuesday through Saturday, 10am to 6pm on 2026-08-25. */}
        <div
          data-reveal
          className="bb-visit-line"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            marginTop: "clamp(28px,3.5vw,40px)",
            fontSize: "15.5px",
            color: color.slate,
          }}
        >
          <a
            href={shop.mapHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${shop.addressShort} — open in Google Maps`}
            className="bb-underline-grow"
            style={{ fontWeight: 700, color: color.ink }}
          >
            {shop.addressShort}
          </a>
          {/* The dot rides WITH the hours rather than sitting between the two as
              its own flex item. As a sibling it is the last thing on line one
              below ~430px — measured: at 390 the row wraps to
              [address, dot] / [hours] — a bullet pointing at nothing. Nested
              here it wraps down with the text it introduces, and
              `.bb-visit-dot` hides it outright once the two stack, because a
              separator between two things on separate lines separates nothing.
              See the media query in app/frontend/styles/bluebird.css. */}
          <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              className="bb-visit-dot"
              aria-hidden="true"
              style={{ width: "4px", height: "4px", borderRadius: "50%", background: color.slateLight }}
            />
            {shop.hoursOpen}
          </span>
        </div>
      </Wrap>
    </section>
  );
}

/* --- Bike grid ------------------------------------------------------------ */

/**
 * One card.
 *
 * The hover swap — side profile becomes the same bike in use — is the comp's
 * strongest merchandising idea, and it survives on touch: with a coarse
 * pointer there is no hover, so the comp reveals the second frame while the
 * card is centered in the viewport. That behavior is reimplemented here rather
 * than ported from `support.js`.
 */
function BikeCard({ bike }: { bike: GridBike }) {
  const ref = useRef<HTMLDivElement>(null);
  const [touchShown, setTouchShown] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const imageCandidates = [
    bike.imageUrl ? {
      url: bike.imageUrl,
      srcSet: bike.imageSrcSet,
      altText: bike.imageAlt,
      width: bike.imageWidth,
      height: bike.imageHeight,
      ground: bike.imageGround,
      fit: bike.imageFit,
    } : undefined,
    ...(bike.imageFallbacks || []),
  ].filter((image): image is NonNullable<typeof image> => Boolean(image?.url));
  const image = imageCandidates[imageIndex];

  useEffect(() => {
    const coarse = window.matchMedia?.("(pointer: coarse)").matches;
    if (!coarse || !ref.current || !("IntersectionObserver" in window)) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setTouchShown(e.intersectionRatio > 0.65)),
      { threshold: [0, 0.65, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div data-reveal="rise" data-reveal-stagger="" ref={ref}>
      <A
        href={bike.href}
        className="bb-bike-card"
        style={{
          display: "block",
          position: "relative",
          aspectRatio: "4/3",
          borderRadius: radius.card,
          overflow: "hidden",
          background: `repeating-linear-gradient(45deg,${color.surface},${color.surface} 12px,${color.hatchLight} 12px,${color.hatchLight} 24px)`,
        }}
      >
        {image ? (
          <img
            src={image.url}
            srcSet={image.srcSet}
            sizes="(max-width: 700px) 92vw, 420px"
            alt={image.altText || bike.name}
            loading="lazy"
            width={image.width || undefined}
            height={image.height || undefined}
            onError={() => setImageIndex((current) => current + 1)}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              // A PRODUCT is contained, never cropped. Studio aspects cluster
              // at 1.33-1.78 against this card's 4/3, so contain wastes almost
              // nothing — and a product shot with the front wheel sliced off is
              // a worse failure than any background mismatch.
              objectFit: image.fit || "contain",
              // The card's ground is painted to match the photograph's own,
              // measured at ingest. The defect a viewer actually notices is the
              // SEAM where the photo's background meets the card's, not the
              // background itself; matching them removes the edge. Null means
              // either a transparent cutout or an art-directed scene whose
              // ground is not flat (Benno) — both get plain white.
              background: image.ground || color.white,
              display: "block",
            }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", padding: "12px" }}>
            <span
              style={{
                fontFamily: font.mono,
                fontSize: "11px",
                color: color.slate,
                background: "rgba(255,255,255,.8)",
                padding: "3px 7px",
                borderRadius: radius.chip,
              }}
            >
              {bike.shot}
            </span>
          </div>
        )}
        {/*
          The second frame, revealed on hover — the comp's strongest
          merchandising idea. The studio shot answers "what is it"; this one
          answers "what is it like to own one", which is the question somebody
          browsing cargo bikes actually has.

          THE RULE: render this ONLY when a genuinely different second image
          exists. Four of the 44 listings have no lifestyle frame and never will
          (Tern Quick Haul Long, R&M Packster 70 vario, Flyer Via, Flyer Flex —
          the manufacturers either never published one or deleted it). Those
          four cards are quietly static, which nobody notices. A hover that
          cross-fades to an IDENTICAL image reads as a rendering bug, which
          everybody notices.

          This condition used to be `!bike.imageUrl`, which meant the reveal
          worked on placeholder hatches and switched itself off the moment real
          photography arrived — exactly backwards, and invisible to the pixel
          diff because that harness runs the image-less fixture.
        */}
        {(bike.hoverUrl || !image) && (
        <div
          className="bb-card-overlay"
          style={{
            position: "absolute",
            inset: 0,
            // No hover on a touch device, so the comp reveals the second frame
            // on scroll instead; BikeCard reimplements that with an
            // IntersectionObserver at 65% visibility.
            opacity: touchShown ? 1 : undefined,
            background: bike.hoverUrl
              ? undefined
              : `repeating-linear-gradient(45deg,${color.hatchOverlayLight},${color.hatchOverlayLight} 12px,${color.hatchOverlayDark} 12px,${color.hatchOverlayDark} 24px)`,
            display: "flex",
            alignItems: "flex-end",
            padding: "12px",
          }}
        >
          {bike.hoverUrl ? (
            <img
              src={bike.hoverUrl}
              srcSet={bike.hoverSrcSet}
              sizes="(max-width: 700px) 92vw, 420px"
              alt=""
              aria-hidden="true"
              loading="lazy"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                // A SCENE is covered, always cropped. Lifestyle aspects run
                // 0.56-2.63 — a five-fold spread including 12 portrait frames —
                // and containing that would letterbox half the library. A scene
                // has no "wrong" crop the way a product does, so `cover` also
                // makes the portrait case disappear rather than need a rule.
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <span
              style={{
                fontFamily: font.mono,
                fontSize: "11px",
                color: color.overlayInk,
                background: "rgba(255,255,255,.85)",
                padding: "3px 7px",
                borderRadius: radius.chip,
              }}
            >
              {bike.shotInUse}
            </span>
          )}
        </div>
        )}
      </A>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "12px",
          marginTop: "8px",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: "19px" }}>
          <A
            href={bike.href}
            className="bb-bike-title"
            style={{ color: color.ink, display: "inline-block", padding: "6px 0" }}
          >
            {bike.name}
          </A>
        </div>
        {/* Price is shown only while the bike is available. A price on a bike
            nobody can buy is an invitation to be disappointed. */}
        {bike.available && (
          <div style={{ fontSize: "17px", color: color.inkMuted, flex: "none" }}>{bike.price}</div>
        )}
      </div>
      {/* Unavailable is the ONLY state that gets a badge — in-stock renders
          nothing — and the badge is a real action, never a dead button. */}
      {bike.sold && (
        <div data-reveal data-reveal-delay="450" style={{ marginTop: "4px", fontSize: "14px", color: color.soldRed }}>
          {home.bikes.soldPrefix}
          <A
            href={cta.testRideHref}
            className="bb-underline-grow bb-underline-grow-red"
            style={{ fontWeight: 700 }}
          >
            {home.bikes.soldLink}
          </A>
        </div>
      )}
    </div>
  );
}

export function BikeGrid({ bikes }: { bikes: GridBike[] }) {
  return (
    <section
      id="bikes"
      data-section="bikes"
      style={{
        maxWidth,
        margin: "0 auto",
        padding: `clamp(56px,8vw,100px) ${space.gutter} clamp(32px,5vw,56px)`,
      }}
    >
      <div
        data-reveal
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "8px 24px",
        }}
      >
        <H2>{home.bikes.heading}</H2>
        <A href="/bikes" className="bb-arrow-link" style={{ fontWeight: 700, fontSize: "16px" }}>
          {home.bikes.seeAll}
        </A>
      </div>
      <div style={{ margin: "10px 0 36px", fontSize: "15px", color: color.slate }}>
        {home.bikes.brands}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(min(280px,100%),1fr))",
          gap: `${space.gridRow} ${space.gridCol}`,
        }}
      >
        {bikes.map((b) => (
          <BikeCard key={b.name} bike={b} />
        ))}
      </div>
      <BrandBoard />
    </section>
  );
}

/**
 * The current seven brands, as a grid.
 *
 * Aaron asked for this after seeing a peer shop's version
 * (docs/design/liked-elements/shots/brand-grid.jpg): a block of brand cells that
 * says "this is a real dealer" faster than a sentence can. The homepage carried
 * the same information as one line of small grey text under the heading, which
 * a visitor scanning for "do they sell the bike I want" does not read.
 *
 * WORDMARKS, NOT LOGO FILES, and that is deliberate.
 *
 * public/images/brands/ holds four SVGs — and they are the wrong four. It has
 * YUBA, which the shop does not carry (already caught once in the /bikes meta
 * description), and it is missing Gazelle and Flyer, which it does. Shipping
 * that set would advertise a brand Ryan cannot sell and omit two he can.
 *
 * Set in the design's own typeface, every cell is correct, consistent, sharp at
 * any size, and costs no requests. When Ryan supplies the real dealer logo pack
 * — seven files, current, with permission — this becomes an <img> grid and the
 * layout does not move.
 *
 * Each cell links into the catalog rather than out to the manufacturer: the job
 * of this block is to get somebody to the bikes, not to send them to Tern.
 */
function BrandBoard() {
  return (
    <div style={{ marginTop: "clamp(44px,6vw,72px)" }} data-reveal>
      <div
        style={{
          fontFamily: font.mono,
          fontSize: "12px",
          letterSpacing: ".14em",
          textTransform: "uppercase",
          color: color.slate,
          marginBottom: "18px",
        }}
      >
        {home.bikes.brandBoardLabel}
      </div>
      <div
        className="bb-brand-board"
        style={{
          display: "grid",
          // Seven brands balance best as four cells followed by three. The
          // 560px media query switches this to two columns on small screens.
          gridTemplateColumns: "repeat(4,1fr)",
          // 1px of border showing through the gaps draws the cell grid, so
          // there is no double border where two cells meet.
          gap: "1px",
          background: color.border,
          border: `1px solid ${color.border}`,
          borderRadius: radius.card,
          overflow: "hidden",
        }}
      >
        {home.bikes.brandList.map((b) => (
          <A
            key={b.slug}
            href={`/bikes?brand[]=${b.slug}`}
            className="bb-brand-cell"
            style={{
              background: color.white,
              color: color.ink,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "clamp(22px,3vw,34px) 16px",
              fontWeight: 800,
              fontSize: "clamp(15px,1.6vw,18px)",
              letterSpacing: ".02em",
              textAlign: "center",
              textTransform: "uppercase",
            }}
          >
            {b.name}
          </A>
        ))}
      </div>
    </div>
  );
}

/* --- Savings band --------------------------------------------------------- */

/**
 * The dollar figures count up when they scroll into view, as in the comp.
 *
 * Reduced motion, or no IntersectionObserver, and they simply render at their
 * final value — the number is the information, the animation is decoration.
 */
function CountUp({ target, display }: { target: number; display: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [text, setText] = useState(display);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) return;

    let raf = 0;
    let ran = false;
    const fmt = (n: number) => "$" + Math.round(n).toLocaleString("en-US");
    const run = () => {
      if (ran) return;
      ran = true;
      const dur = 1100;
      const t0 = performance.now();
      const step = () => {
        const p = Math.min(1, (performance.now() - t0) / dur);
        setText(fmt(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(step);
        else setText(display);
      };
      setText(fmt(0));
      raf = requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          run();
        }),
      { threshold: 0.4 }
    );
    io.observe(el);
    // The comp's own belt-and-braces: if the observer never fires, land on the
    // real number anyway.
    const land = window.setTimeout(run, 4000);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      window.clearTimeout(land);
    };
  }, [target, display]);

  return (
    <div
      ref={ref}
      style={{
        fontWeight: 800,
        fontSize: type.figure,
        lineHeight: 0.9,
        color: color.blue,
        letterSpacing: "-.02em",
      }}
    >
      {text}
    </div>
  );
}

export function SavingsBand() {
  const [first, second] = home.savings.rows;
  const rule = "2px solid rgba(19,26,44,.16)";
  return (
    <section
      data-section="savings"
      style={{
        maxWidth,
        margin: "0 auto",
        padding: `0 ${space.gutter} clamp(56px,8vw,100px)`,
      }}
    >
      <div
        data-reveal="rise"
        style={{
          background: color.gold,
          borderRadius: radius.panel,
          padding: "clamp(28px,4vw,48px)",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))",
          gap: `clamp(28px,4vw,48px) clamp(32px,5vw,64px)`,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", alignItems: "flex-start" }}>
          <H2 style={{ fontSize: type.h2Tight, color: color.ink, maxWidth: "22ch" }}>
            {home.savings.heading}
          </H2>
          <p style={{ margin: 0, fontSize: "16.5px", lineHeight: 1.55, color: color.onGold, maxWidth: "38ch" }}>
            {home.savings.body}
          </p>
          <Button href="/savings" variant="blue" style={{ fontSize: "16px", padding: "15px 26px" }}>
            {home.savings.cta}
          </Button>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            columnGap: "20px",
            rowGap: 0,
            alignContent: "start",
          }}
        >
          <div style={{ padding: "0 0 20px", borderBottom: rule }}>
            <CountUp target={first.countUp} display={first.figure} />
          </div>
          <div
            style={{
              fontSize: "15px",
              lineHeight: 1.45,
              color: color.onGold,
              padding: "0 0 20px",
              borderBottom: rule,
            }}
          >
            <span style={{ fontWeight: 700, display: "block", color: color.ink }}>{first.title}</span>
            {first.body}
          </div>
          <div style={{ padding: "20px 0 0" }}>
            <CountUp target={second.countUp} display={second.figure} />
          </div>
          <div style={{ fontSize: "15px", lineHeight: 1.45, color: color.onGold, padding: "20px 0 0" }}>
            <span style={{ fontWeight: 700, display: "block", color: color.ink }}>{second.title}</span>
            {second.body}
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- Shop band ------------------------------------------------------------ */

export function ShopBand() {
  return (
    <section id="shop" data-section="shop" style={{ background: color.blue, color: color.white }}>
      <div
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `${space.sectionY} ${space.gutter}`,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))",
          gap: "clamp(32px,5vw,72px)",
          alignItems: "center",
        }}
      >
        {/*
          The shop itself — one of Ryan's approved Google Business photographs.
          `docs/design/PHOTOGRAPHY-SOURCING-2026-08-14.md`
          found that what makes a bike shop read as local is a handful of photos
          of its own building; no manufacturer asset can do this job.

          It goes here rather than in the hero because it is an interior full of
          cargo bikes, while the hero's job is to show a family using one.

          The slot keeps its exact geometry so script/design_diff.mjs still
          measures the same box; the photograph fills it.
        */}
        <PhotoSlot
          label={home.shopBand.photoNote}
          tone="blue"
          data-reveal="image"
          style={{ height: "clamp(280px,34vw,420px)", borderRadius: radius.card }}
        >
          <img
            src="/images/place/shop-floor-1400.jpg"
            srcSet={
              "/images/place/shop-floor-900.jpg 900w, " +
              "/images/place/shop-floor-1400.jpg 1400w, " +
              "/images/place/shop-floor-2000.jpg 2000w"
            }
            sizes="(max-width: 700px) 92vw, 560px"
            alt="Cargo bikes lined up inside the Front Range Cargo Bikes shop"
            loading="lazy"
            width={2000}
            height={1500}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              // A place photograph is a scene: cropped to the slot, never
              // letterboxed. Same rule the lifestyle frames follow.
              objectFit: "cover",
              borderRadius: radius.card,
              display: "block",
            }}
          />
        </PhotoSlot>
        <div>
          <H2 data-reveal>{home.shopBand.heading}</H2>
          <p style={{ margin: "20px 0 0", fontSize: "17px", lineHeight: 1.6, color: color.onBlue }}>
            {home.shopBand.body1}
          </p>
          <p style={{ margin: "16px 0 0", fontSize: "17px", lineHeight: 1.6, color: color.onBlue }}>
            {home.shopBand.body2}
          </p>
          <div
            style={{
              display: "flex",
              gap: "14px",
              flexWrap: "wrap",
              marginTop: "26px",
              alignItems: "center",
            }}
          >
            <Button href={cta.maintenanceHref} variant="gold" style={{ fontSize: "15px", padding: "15px 24px" }}>
              {home.shopBand.primary}
            </Button>
            <A
              href="/about"
              className="bb-arrow-link bb-arrow-link-gold"
              style={{ color: color.gold, fontWeight: 700, fontSize: "16px" }}
            >
              {home.shopBand.secondary}
            </A>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- Testimonials -------------------------------------------------------- */

const REVIEW_ACCENTS = [color.blue, color.gold, color.red] as const;

/** Verified Google excerpts; the rating total and source URL live in one file. */
export function Testimonials() {
  const reviews = googleReviewProof.reviews;
  const reviewCount = reviews.length;
  const viewportRef = useRef<HTMLDivElement>(null);
  const snapNextScrollRef = useRef(false);
  // Target of the last programmatic scroll. While set, scroll events belong to
  // that animation, however long a slow environment stretches it — a timed
  // guard proved flaky on the deployed host. Cleared when the viewport lands
  // within a couple of pixels, or by a real pointer interaction.
  const programmaticTargetRef = useRef<number | null>(null);
  const [position, setPosition] = useState({ active: 0, physical: 1 });
  const [hoverPaused, setHoverPaused] = useState(false);
  const [focusPaused, setFocusPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  // Web fonts land after mount and shift card geometry by a few pixels; the
  // one-shot centering scroll then points at a stale offset. Re-run it once
  // the fonts are in.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready?.then(() => { if (!cancelled) setFontsReady(true); });
    return () => { cancelled = true; };
  }, []);

  // The rendered track has one clone at either end. Crossing an edge moves to
  // the adjacent clone first; after that visible slide finishes, the viewport
  // silently resets to the matching real card. Visitors can keep moving in
  // either direction without seeing a long jump across the whole track.
  const loopedReviews = [
    { review: reviews[reviewCount - 1], logicalIndex: reviewCount - 1, clone: true, key: "before" },
    ...reviews.map((review, logicalIndex) => ({
      review,
      logicalIndex,
      clone: false,
      key: `review-${logicalIndex}`,
    })),
    { review: reviews[0], logicalIndex: 0, clone: true, key: "after" },
  ];

  const moveBy = useCallback(
    (delta: -1 | 1) => {
      setPosition((current) => {
        // Hold input for the fraction of a second spent moving through a clone;
        // the edge-reset effect below immediately unlocks the canonical card.
        if (current.physical === 0 || current.physical === reviewCount + 1) return current;

        const active = (current.active + delta + reviewCount) % reviewCount;
        let physical = active + 1;
        if (!reducedMotion && current.active === 0 && delta === -1) physical = 0;
        if (!reducedMotion && current.active === reviewCount - 1 && delta === 1) physical = reviewCount + 1;
        return { active, physical };
      });
    },
    [reducedMotion, reviewCount],
  );

  const moveTo = useCallback(
    (index: number) => {
      const active = (index + reviewCount) % reviewCount;
      setPosition({ active, physical: active + 1 });
    },
    [reviewCount],
  );

  // The viewport is a real horizontal scroller, so a touch swipe moves cards
  // without ever calling moveBy. Without this the dots, aria labels and
  // autoplay all stay pinned to whatever the last button press selected.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    let settle = 0;
    const clearTarget = () => { programmaticTargetRef.current = null; };
    const onScroll = () => {
      // A smooth programmatic scroll emits the same events a swipe does. Reading
      // the midpoint while one is still travelling would snap to whatever card
      // happened to be passing — and on a slow host that animation outlives any
      // fixed timer, so ownership ends only when the target is reached.
      const target = programmaticTargetRef.current;
      if (target !== null) {
        if (Math.abs(viewport.scrollLeft - target) <= 2) programmaticTargetRef.current = null;
        return;
      }
      window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        const cards = Array.from(
          viewport.querySelectorAll<HTMLElement>("[data-carousel-position]"),
        );
        if (cards.length === 0) return;

        const viewportCenter = viewport.getBoundingClientRect().left + viewport.clientWidth / 2;
        let nearest = 0;
        let nearestDistance = Infinity;
        cards.forEach((card, index) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - viewportCenter);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = index;
          }
        });

        setPosition((current) => {
          // A clone position belongs to the edge-reset timer. Adopting a
          // scroll-derived position here would clear that timer and strand
          // the carousel on the clone (seen as a CI-only race on slow runs).
          if (current.physical === 0 || current.physical === reviewCount + 1) return current;
          if (current.physical === nearest) return current;
          const logical = ((nearest - 1 + reviewCount) % reviewCount + reviewCount) % reviewCount;
          return { active: logical, physical: nearest };
        });
      }, 140);
    };

    viewport.addEventListener("scroll", onScroll, { passive: true });
    // A real pointer gesture takes ownership back immediately, so a user swipe
    // that interrupts an autoplay animation still syncs.
    viewport.addEventListener("pointerdown", clearTarget, { passive: true });
    viewport.addEventListener("wheel", clearTarget, { passive: true });
    viewport.addEventListener("touchstart", clearTarget, { passive: true });
    return () => {
      window.clearTimeout(settle);
      viewport.removeEventListener("scroll", onScroll);
      viewport.removeEventListener("pointerdown", clearTarget);
      viewport.removeEventListener("wheel", clearTarget);
      viewport.removeEventListener("touchstart", clearTarget);
    };
  }, [reviewCount]);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(query.matches);
    sync();
    query.addEventListener?.("change", sync);
    return () => query.removeEventListener?.("change", sync);
  }, []);

  useEffect(() => {
    const sync = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", sync);
    sync();
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  useEffect(() => {
    if (reducedMotion || hoverPaused || focusPaused || tabHidden) return;
    const timer = window.setInterval(() => moveBy(1), 7000);
    return () => window.clearInterval(timer);
  }, [focusPaused, hoverPaused, moveBy, reducedMotion, tabHidden]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const card = viewport?.querySelector<HTMLElement>(`[data-carousel-position="${position.physical}"]`);
    if (!viewport || !card) return;
    const cardLeft = card.getBoundingClientRect().left - viewport.getBoundingClientRect().left + viewport.scrollLeft;
    const shouldSnap = snapNextScrollRef.current || reducedMotion;
    snapNextScrollRef.current = false;
    if (shouldSnap) viewport.style.scrollBehavior = "auto";
    const targetLeft = cardLeft - (viewport.clientWidth - card.offsetWidth) / 2;
    programmaticTargetRef.current = targetLeft;
    viewport.scrollTo({ left: targetLeft, behavior: shouldSnap ? "auto" : "smooth" });
    if (shouldSnap) {
      window.requestAnimationFrame(() => {
        viewport.style.scrollBehavior = "";
      });
    }

    if (reducedMotion || (position.physical !== 0 && position.physical !== reviewCount + 1)) return;
    const timer = window.setTimeout(() => {
      snapNextScrollRef.current = true;
      setPosition((current) => ({
        active: current.active,
        physical: current.physical === 0 ? reviewCount : 1,
      }));
    }, 450);
    return () => window.clearTimeout(timer);
  }, [position.physical, reducedMotion, reviewCount, fontsReady]);

  return (
    <section data-section="testimonials" style={{ background: color.surface }}>
      <Wrap padY={space.sectionY}>
        <div data-reveal style={{ textAlign: "center" }}>
          <div
            style={{
              fontFamily: font.mono,
              fontSize: "12px",
              letterSpacing: ".14em",
              textTransform: "uppercase",
              color: color.blue,
            }}
          >
            {home.testimonials.eyebrow}
          </div>
          <H2 style={{ margin: "14px auto 0", textWrap: "balance" }}>{home.testimonials.heading}</H2>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <GoogleRatingBadge variant="section" />
          </div>
        </div>

        <div
          className="bb-review-carousel"
          role="region"
          aria-roledescription="carousel"
          aria-label="Google reviews from local families"
          data-carousel-active-index={position.active}
          data-carousel-physical-index={position.physical}
          data-carousel-review-count={reviewCount}
          tabIndex={0}
          onMouseEnter={() => setHoverPaused(true)}
          onMouseLeave={() => setHoverPaused(false)}
          onFocusCapture={() => setFocusPaused(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusPaused(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              moveBy(-1);
            }
            if (event.key === "ArrowRight") {
              event.preventDefault();
              moveBy(1);
            }
          }}
          style={{
            marginTop: "clamp(32px,4vw,48px)",
            outlineOffset: "6px",
          }}
        >
          <div ref={viewportRef} className="bb-review-viewport" aria-live="off">
            <div className="bb-review-track">
          {loopedReviews.map(({ review, logicalIndex, clone, key }, physicalIndex) => (
            <article
              key={key}
              data-review-index={logicalIndex}
              data-carousel-position={physicalIndex}
              data-review-clone={clone ? "true" : "false"}
              data-active={physicalIndex === position.physical ? "true" : "false"}
              aria-hidden={clone ? true : undefined}
              aria-label={clone ? undefined : `Review ${logicalIndex + 1} of ${reviewCount}`}
              className="bb-quote-card"
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: "230px",
                padding: "clamp(24px,3vw,32px)",
                background: color.white,
                border: `1px solid ${color.borderCard}`,
                borderTop: `5px solid ${REVIEW_ACCENTS[logicalIndex % REVIEW_ACCENTS.length]}`,
                borderRadius: radius.panel,
                boxShadow: shadow.card,
              }}
            >
              <StarRating value={review.rating} size={18} />
              <blockquote cite={googleReviewProof.listingHref} style={{ margin: "22px 0 0" }}>
                <p style={{ margin: 0, fontSize: "17px", lineHeight: 1.62, color: color.ink }}>
                  “{review.quote}”
                </p>
              </blockquote>
              <footer
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "8px 18px",
                  marginTop: "auto",
                  paddingTop: "22px",
                  borderTop: `1px solid ${color.borderFaint}`,
                  fontSize: "14px",
                }}
              >
                <span style={{ fontWeight: 800, color: color.ink }}>— {review.author}</span>
                <a
                  href={googleReviewProof.listingHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={clone ? -1 : undefined}
                  className="bb-review-source"
                  aria-label={`Open ${review.author}’s Google review in a new tab`}
                  style={{ color: color.blue, fontWeight: 700 }}
                >
                  Google review ↗
                </a>
              </footer>
            </article>
          ))}
            </div>
          </div>

          <div className="bb-review-controls" aria-label="Review controls">
            <button type="button" className="bb-review-arrow" aria-label="Previous review" onClick={() => moveBy(-1)}>‹</button>
            <div className="bb-review-dots" aria-label={`Review ${position.active + 1} of ${reviewCount}`}>
              {reviews.map((review, index) => (
                <button
                  key={review.author}
                  type="button"
                  className="bb-review-dot"
                  data-active={index === position.active ? "true" : "false"}
                  aria-label={`Show review ${index + 1}`}
                  aria-current={index === position.active ? "true" : undefined}
                  onClick={() => moveTo(index)}
                />
              ))}
            </div>
            <div
              className="bb-review-counter"
              aria-label={`Review ${position.active + 1} of ${reviewCount}`}
            >
              {position.active + 1} / {reviewCount}
            </div>
            <button type="button" className="bb-review-arrow" aria-label="Next review" onClick={() => moveBy(1)}>›</button>
          </div>
        </div>

        <div data-reveal style={{ textAlign: "center", marginTop: "clamp(28px,4vw,42px)" }}>
          <a
            href={googleReviewProof.listingHref}
            target="_blank"
            rel="noopener noreferrer"
            className="bb-arrow-link"
            style={{ color: color.blue, fontSize: "16px", fontWeight: 800 }}
          >
            Read all {googleReviewProof.reviewCount} Google reviews →
          </a>
        </div>
      </Wrap>
    </section>
  );
}
